// sales-page/src/core/salesApi.js
import axios from 'axios';

const _rawBase = import.meta.env.VITE_API_URL || 'http://localhost:5000';
const API_BASE_URL = _rawBase.endsWith('/api') ? _rawBase : _rawBase.replace(/\/+$/, '') + '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const salesApi = {

  // ─── PHASE 1: VALIDATE PROMOTER CODE ──────────────────────────────────────
  // Called on page load before anything else renders.
  // Returns { success, valid, data: { promoter_code, promoter_name } } on success
  // Throws on network error or 4xx/5xx
  validatePromoterCode: async (promoterCode) => {
    try {
      const response = await api.get(`/public/promo/validate/${promoterCode}`);
      return response.data;
    } catch (error) {
      console.error('Validate promoter code error:', error);
      throw error;
    }
  },

  // ─── CAMPAIGN STATUS CHECK ─────────────────────────────────────────────────
  // Called after code validation to check if the active campaign is closed.
  // Returns { campaign_closed, campaign: { name, status, ... } }
  getCampaignStatus: async (promoterCode) => {
    try {
      const response = await api.get(`/public/promo/landing/${promoterCode}`);
      return response.data;
    } catch (error) {
      console.error('Get campaign status error:', error);
      throw error;
    }
  },

  // ─── PHASE 3: PARTNER SLOT COUNTER ────────────────────────────────────────
  // Called by PartnerInterstitial on load to check remaining spots (cap = 100)
  // Returns { success, data: { total_partners, spots_remaining, is_full, max_partners } }
  getPartnerCount: async () => {
    try {
      const response = await api.get('/partner-count');
      return response.data;
    } catch (error) {
      console.error('Get partner count error:', error);
      throw error;
    }
  },

  trackClick: async (clickData) => {
    try {
      const response = await api.post('/public/promo/track-click', clickData);
      return response.data;
    } catch (error) {
      console.error('Track click error:', error);
      throw error;
    }
  },

  sendOTP: async (otpData) => {
    try {
      const response = await api.post('/public/promo/send-otp', otpData);
      return response.data;
    } catch (error) {
      console.error('Send OTP error:', error);
      throw error;
    }
  },

  verifyOTP: async (verificationData) => {
    try {
      const response = await api.post('/public/promo/verify-otp', verificationData);
      return response.data;
    } catch (error) {
      console.error('Verify OTP error:', error);
      throw error;
    }
  },

  resendOTP: async (resendData) => {
    try {
      const response = await api.post('/public/promo/resend-otp', resendData);
      return response.data;
    } catch (error) {
      console.error('Resend OTP error:', error);
      throw error;
    }
  },

  createCheckoutSession: async (checkoutData) => {
    try {
      const response = await api.post('/create-checkout-session', checkoutData);
      return response.data;
    } catch (error) {
      console.error('Create checkout session error:', error);
      throw error;
    }
  },

  getPromoterCode: () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('ref') || params.get('promo') || params.get('promoter') || params.get('code') || null;
  },

  storeVisitorData: (data) => {
    try {
      localStorage.setItem('visitor_fingerprint', data.fingerprint);
      localStorage.setItem('promoter_code', data.promoter_code);
      localStorage.setItem('geo_data', JSON.stringify(data.geo_data || {}));
    } catch (error) {
      console.error('Store visitor data error:', error);
    }
  },

  getStoredVisitorData: () => {
    try {
      return {
        fingerprint: localStorage.getItem('visitor_fingerprint'),
        promoter_code: localStorage.getItem('promoter_code'),
        geo_data: JSON.parse(localStorage.getItem('geo_data') || '{}')
      };
    } catch (error) {
      console.error('Get stored visitor data error:', error);
      return null;
    }
  },

  storeEmailVerification: (email, verified = false) => {
    try {
      localStorage.setItem('verified_email', email);
      localStorage.setItem('email_verified', verified.toString());
      localStorage.setItem('verification_timestamp', Date.now().toString());
    } catch (error) {
      console.error('Store email verification error:', error);
    }
  },

  getEmailVerification: () => {
    try {
      const email = localStorage.getItem('verified_email');
      const verified = localStorage.getItem('email_verified') === 'true';
      const timestamp = parseInt(localStorage.getItem('verification_timestamp') || '0');
      const isExpired = timestamp && (Date.now() - timestamp > 3600000);
      
      return {
        email,
        verified: verified && !isExpired,
        timestamp
      };
    } catch (error) {
      console.error('Get email verification error:', error);
      return { email: null, verified: false, timestamp: 0 };
    }
  },

  clearEmailVerification: () => {
    try {
      localStorage.removeItem('verified_email');
      localStorage.removeItem('email_verified');
      localStorage.removeItem('verification_timestamp');
    } catch (error) {
      console.error('Clear email verification error:', error);
    }
  },

  // ─── PHASE 3: VERIFY PITCH ACCESS ─────────────────────────────────────────
  // Called by PartnerPitch on mount to confirm the session_id is real (post-checkout)
  // OR verify a drip email token (returning visitor).
  // Returns { success, data: { user_id, customer_id, first_name, email, plan_name, plan_amount } }
  verifyPitchAccess: async ({ session_id, token }) => {
    try {
      const response = await api.post('/verify-pitch-access', { session_id, token });
      return response.data;
    } catch (error) {
      console.error('Verify pitch access error:', error);
      throw error;
    }
  },

  // ─── PHASE 3: ONE-CLICK BADGE CHARGE ──────────────────────────────────────
  // Fires a $200 Payment Intent against the saved Stripe customer_id.
  // No redirect. Returns { success } or throws.
  chargeBadgeUpsell: async ({ customer_id, user_id, email, session_id, token }) => {
    try {
      const response = await api.post('/charge-badge-upsell', {
        customer_id,
        user_id,
        email,
        session_id,
        token
      });
      return response.data;
    } catch (error) {
      console.error('Charge badge upsell error:', error);
      throw error;
    }
  },

  // ─── PHASE 3: SKIP BADGE UPSELL ───────────────────────────────────────────
  // Notifies backend to set badge_status: declined and queue drip sequence.
  // Non-blocking — failure does not stop navigation.
  skipBadgeUpsell: async ({ user_id, session_id, token }) => {
    try {
      const response = await api.post('/skip-badge-upsell', { user_id, session_id, token });
      return response.data;
    } catch (error) {
      console.error('Skip badge upsell error:', error);
      throw error;
    }
  }
};

export default salesApi;